import { Button } from '@/ui/components/button';
import { cn } from '@/ui/utils';
import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { createPortal } from 'react-dom';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

interface Props extends Omit<ErrorComponentProps, 'reset' | 'error'>, React.ComponentProps<'div'> {
  reset?: () => void;
  error?: unknown;
}

export function ErrorComponent({ error, reset, className, ...props }: Props) {
  const router = useRouter();

  useEffect(() => {
    if (!error) return;
    Sentry.captureException(
      error instanceof Error
        ? error
        : Object.assign(
            new Error(
              (error as { message?: string })?.message ?? 'Non-Error thrown in route boundary',
            ),
            { name: (error as { name?: string })?.name ?? 'Error', cause: error },
          ),
    );
  }, [error]);

  return (
    <div className={cn('flex grow flex-col items-center py-10 text-center', className)} {...props}>
      <div>
        <h1 className="mb-3 text-xl">Something went wrong</h1>
        <p className="mb-4 text-balance font-medium text-red-9 text-sm">
          {'We are having technical difficulties. Please, try again.'}
        </p>
        <Button
          // variant={"secondary"}
          onClick={() => {
            if (reset) return reset();
            router.invalidate();
          }}
        >
          Try again
        </Button>
      </div>
      {import.meta.env.DEV && error
        ? createPortal(
            <div className="fixed right-4 bottom-4 mx-auto mt-12 max-h-100 w-full max-w-150 overflow-auto rounded-md border border-neutral bg-surface-3 p-4 text-left">
              <h2 className="mb-2 text-lg text-red-9">Error</h2>
              {error instanceof Error ? error.message : String(error)}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
